import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { StepperDialogComponent } from '@serviceplan/ui-kit';
import {
  ClientEnvironment,
  ClientNotificationHandlerService,
  ENVIRONMENT,
  MultipleAgencyClientBrand,
  StepperModel,
  ValueListsService,
  WidthClass,
} from '@thehubz/client/core';
import { Collection, CollectionsApiService, CompanyBaseDataWithClients } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';
import { EMPTY, lastValueFrom, map, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CollectionPublishingStepperService {
  brandingKey!: CustomClients;
  CustomClients = CustomClients;

  constructor(
    @Inject(ENVIRONMENT) env: ClientEnvironment,
    private dialog: MatDialog,
    private valueListsService: ValueListsService,
    protected collectionAPIService: CollectionsApiService,
    private readonly clientNotificationHandlerService: ClientNotificationHandlerService,
  ) {
    this.brandingKey = env.brandingKey;
  }

  /**
   * Create stepper model for publishing
   * @param stepLabel
   * @returns
   */
  private async createStepperModel(stepLabel: string): Promise<StepperModel> {
    const listUserAgencies = (await lastValueFrom(this.valueListsService.listUserAgencies()))
      .filter((agency) => agency.clients.length)
      .map((agency) => ({ ...agency, selected: false }));

    let agencyLable = '';
    let clientsLabel = '';

    switch (this.brandingKey) {
      case CustomClients.Vorwerk: {
        agencyLable = $localize`Domain`;
        clientsLabel = $localize`Location & Brands`;
        break;
      }
      case CustomClients.SEAT: {
        agencyLable = $localize`Agency`;
        clientsLabel = $localize`Marke & Model`;
        break;
      }
      case CustomClients.LufthansaMediacenter:
      case CustomClients.LufthansaMedialounge:
      default: {
        agencyLable = $localize`Agency`;
        clientsLabel = $localize`Clients & Brands`;
        break;
      }
    }

    const model: StepperModel = {
      stepID: 'shareModel',
      stepLabel: stepLabel,
      stepper: [
        {
          stepperId: '1',
          stepperName: $localize`Information`,
          stepperGroupName: 'information',
          hideHeader: true,
          selected: true,
          visited: true,
          formFields: [
            {
              formTypeId: '13',
              formFieldName: 'publishCollection',
              fieldType: 'formArray',
              fieldLabel: '',
              placeholder: '',
              inputType: '',
              mandatory: true,
              widthClass: WidthClass.WFull,
              disable: false,
              visible: true,
              formFieldGroup: [
                {
                  formTypeId: '1',
                  formFieldName: 'agency',
                  fieldType: 'dropdown',
                  fieldLabel: agencyLable,
                  placeholder: agencyLable,
                  widthClass: WidthClass.W12,
                  options: listUserAgencies,
                  validators: {
                    required: true,
                  },
                  value: '',
                  visible: true,
                },
                {
                  formTypeId: '1',
                  formFieldName: 'clientNBrand',
                  fieldType: 'treeHierarchy',
                  fieldLabel: clientsLabel,
                  widthClass: WidthClass.W12,
                  validators: {
                    required: true,
                  },
                  visible: true,
                  value: '',
                },
              ],
            },
          ],
          actions: [
            {
              actionLabel: $localize`Publish`,
              actionTypePosition: 'right',
              icon: CustomClients.Vorwerk === this.brandingKey ? '' : 'send',
              alignIcon: 'right',
              actionType: 'publishCollection',
              disable: false,
              visible: false,
            },
          ],
        },
      ],
    };
    return model;
  }

  /**
   * open stepper dialog for publish collection
   */
  async collectionPublishingStepper(collection: Collection) {
    const stepLabel = $localize`Publish collection` + `: <span class="font-light">${collection.name}<span>`;
    const stepperData = await this.createStepperModel(stepLabel);
    const collectionPublished = this.transformModel(collection.published || []);

    const dialogRef = this.dialog.open(StepperDialogComponent, {
      width: '90%',
      height: '90%',
      maxWidth: '100vw',
      disableClose: true,
      panelClass: 'custom-dialog-container',
      data: {
        stepperData,
        additionalData: collectionPublished,
        callFrom: 'publishCollection',
        editable: collection.published?.length ? true : false,
      },
    });
    dialogRef.componentInstance.matTreeCheckbox = true;
    // after dialog close create sharing collection in db
    return dialogRef.afterClosed().pipe(
      map((result: MultipleAgencyClientBrand) => Object.values(result)),
      switchMap((published: AnyType[]) =>
        this.collectionAPIService.collectionsControllerPublish(collection._id, { published }).pipe(
          switchMap((coll) => {
            if (coll) {
              this.clientNotificationHandlerService.successNotification($localize`Collection "${collection.name}" has been published`);
              return of(coll);
            } else {
              return EMPTY;
            }
          }),
        ),
      ),
    );
  }

  /**
   * Transform collection publish model CompanyBaseDataWithClients to Agency Client and Brand (_id, name) model
   * @param published
   * @returns
   */
  transformModel(published: CompanyBaseDataWithClients[]) {
    if (published.length) {
      return published.map((company) => ({
        _id: company.key,
        name: company.name,
        clients: company.clients?.map((client) => ({
          _id: client.key,
          name: client.name,
          brands: client.brands?.map((brand) => ({ _id: brand.key, name: brand.name })),
        })),
      }));
    }
    return [];
  }
}
